import React, { useState, useEffect, useRef } from "react";

type Props = {};

// Game settings and types
type Difficulty = "easy" | "medium" | "hard";
type FieldLength = "short" | "medium" | "long";
type SpacecraftType = {
  id: string;
  name: string;
  color: string;
  speed: number;
  maneuverability: number;
  image: string;
};

// Game objects
type Player = {
  x: number;
  y: number;
  width: number;
  height: number;
  speed: number;
  maneuverability: number;
  spacecraft: SpacecraftType;
};

type Asteroid = {
  id: string;
  x: number;
  y: number;
  size: number;
  speed: number;
  rotation: number;
  rotationSpeed: number;
};

type CosmicDust = {
  id: string;
  x: number;
  y: number;
  size: number;
  speed: number;
  collected: boolean;
  value: number;
};

// Game states
type GameState = "menu" | "setup" | "playing" | "paused" | "gameOver";

export default function CosmicRacerGamePage({}: Props) {
  // Canvas and animation refs
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationFrameRef = useRef<number>(0);

  // Game configuration
  const [gameState, setGameState] = useState<GameState>("menu");
  const [difficulty, setDifficulty] = useState<Difficulty>("medium");
  const [fieldLength, setFieldLength] = useState<FieldLength>("medium");
  const [selectedSpacecraftId, setSelectedSpacecraftId] =
    useState<string>("spacecraft1");

  // Game variables
  const [score, setScore] = useState<number>(0);
  const [distance, setDistance] = useState<number>(0);
  const [player, setPlayer] = useState<Player | null>(null);
  const [asteroids, setAsteroids] = useState<Asteroid[]>([]);
  const [cosmicDusts, setCosmicDusts] = useState<CosmicDust[]>([]);
  const [backgroundSpeed, setBackgroundSpeed] = useState<number>(2);
  const [backgroundPosition, setBackgroundPosition] = useState<number>(0);
  const [keys, setKeys] = useState({
    ArrowLeft: false,
    ArrowRight: false,
    ArrowUp: false,
    ArrowDown: false,
  });

  // Available spacecrafts
  const spacecrafts: SpacecraftType[] = [
    {
      id: "spacecraft1",
      name: "Cosmic Voyager",
      color: "#3498db",
      speed: 5,
      maneuverability: 0.8,
      image:
        "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 50 80'><path d='M25,0 L40,60 L25,80 L10,60 Z' fill='%233498db'/></svg>",
    },
    {
      id: "spacecraft2",
      name: "Star Cruiser",
      color: "#e74c3c",
      speed: 6,
      maneuverability: 0.6,
      image:
        "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 60 80'><path d='M30,0 L50,50 L40,80 L20,80 L10,50 Z' fill='%23e74c3c'/></svg>",
    },
    {
      id: "spacecraft3",
      name: "Nebula Glider",
      color: "#2ecc71",
      speed: 4,
      maneuverability: 1,
      image:
        "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 60 80'><path d='M30,0 L45,30 L60,60 L30,80 L0,60 L15,30 Z' fill='%232ecc71'/></svg>",
    },
    {
      id: "spacecraft4",
      name: "Quantum Dart",
      color: "#f39c12",
      speed: 7,
      maneuverability: 0.5,
      image:
        "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 40 80'><path d='M20,0 L30,60 L20,80 L10,60 Z' fill='%23f39c12'/></svg>",
    },
    {
      id: "spacecraft5",
      name: "Galaxy Hunter",
      color: "#9b59b6",
      speed: 5.5,
      maneuverability: 0.7,
      image:
        "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 60 80'><path d='M30,0 L50,30 L50,60 L30,80 L10,60 L10,30 Z' fill='%239b59b6'/></svg>",
    },
  ];

  // Game settings based on difficulty
  const difficultySettings = {
    easy: {
      asteroidFrequency: 0.02,
      asteroidSpeed: 2,
      asteroidSizeRange: { min: 15, max: 40 },
      dustFrequency: 0.03,
      dustSpeed: 2,
      backgroundSpeed: 2,
    },
    medium: {
      asteroidFrequency: 0.03,
      asteroidSpeed: 3,
      asteroidSizeRange: { min: 20, max: 50 },
      dustFrequency: 0.02,
      dustSpeed: 3,
      backgroundSpeed: 3,
    },
    hard: {
      asteroidFrequency: 0.04,
      asteroidSpeed: 4,
      asteroidSizeRange: { min: 25, max: 60 },
      dustFrequency: 0.015,
      dustSpeed: 4,
      backgroundSpeed: 4,
    },
  };

  // Field length settings
  const fieldLengthSettings = {
    short: 5000,
    medium: 10000,
    long: 20000,
  };

  // Initialize game
  const initializeGame = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const spacecraft =
      spacecrafts.find((s) => s.id === selectedSpacecraftId) || spacecrafts[0];

    setGameState("playing");
    setScore(0);
    setDistance(0);
    setAsteroids([]);
    setCosmicDusts([]);
    setBackgroundPosition(0);
    setBackgroundSpeed(difficultySettings[difficulty].backgroundSpeed);

    setPlayer({
      x: canvas.width / 2 - 25,
      y: canvas.height - 100,
      width: 50,
      height: 80,
      speed: spacecraft.speed,
      maneuverability: spacecraft.maneuverability,
      spacecraft,
    });
  };

  // Handle key presses
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key in keys) {
        setKeys((prevKeys) => ({ ...prevKeys, [e.key]: true }));
      }

      // Pause game with 'p' key
      if (e.key === "p" && gameState === "playing") {
        setGameState("paused");
      } else if (e.key === "p" && gameState === "paused") {
        setGameState("playing");
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key in keys) {
        setKeys((prevKeys) => ({ ...prevKeys, [e.key]: false }));
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [keys, gameState]);

  // Generate random asteroid
  const generateAsteroid = (canvas: HTMLCanvasElement): Asteroid => {
    const settings = difficultySettings[difficulty];
    const size =
      Math.random() *
        (settings.asteroidSizeRange.max - settings.asteroidSizeRange.min) +
      settings.asteroidSizeRange.min;

    return {
      id: Math.random().toString(36).substring(7),
      x: Math.random() * (canvas.width - size),
      y: -size,
      size,
      speed: settings.asteroidSpeed + Math.random() * 2,
      rotation: Math.random() * 360,
      rotationSpeed: Math.random() * 4 - 2,
    };
  };

  // Generate random cosmic dust
  const generateCosmicDust = (canvas: HTMLCanvasElement): CosmicDust => {
    const settings = difficultySettings[difficulty];
    const size = 10 + Math.random() * 10;

    return {
      id: Math.random().toString(36).substring(7),
      x: Math.random() * (canvas.width - size),
      y: -size,
      size,
      speed: settings.dustSpeed + Math.random() * 1.5,
      collected: false,
      value: Math.floor(size),
    };
  };

  // Check collision between two objects
  const checkCollision = (
    obj1: {
      x: number;
      y: number;
      width?: number;
      height?: number;
      size?: number;
    },
    obj2: {
      x: number;
      y: number;
      width?: number;
      height?: number;
      size?: number;
    }
  ) => {
    const obj1Width = obj1.width || obj1.size || 0;
    const obj1Height = obj1.height || obj1.size || 0;
    const obj2Width = obj2.width || obj2.size || 0;
    const obj2Height = obj2.height || obj2.size || 0;

    return (
      obj1.x < obj2.x + obj2Width &&
      obj1.x + obj1Width > obj2.x &&
      obj1.y < obj2.y + obj2Height &&
      obj1.y + obj1Height > obj2.y
    );
  };

  // Main game loop
  useEffect(() => {
    if (gameState !== "playing") return;

    const canvas = canvasRef.current;
    if (!canvas || !player) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const settings = difficultySettings[difficulty];
    const maxDistance = fieldLengthSettings[fieldLength];

    const gameLoop = () => {
      if (!canvas || !ctx || !player) return;

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update background
      drawBackground(ctx, canvas);

      // Update player position based on keys
      let updatedPlayer = { ...player };

      if (keys.ArrowLeft) {
        updatedPlayer.x -= player.speed * player.maneuverability * 1.5;
      }
      if (keys.ArrowRight) {
        updatedPlayer.x += player.speed * player.maneuverability * 1.5;
      }
      if (keys.ArrowUp) {
        updatedPlayer.y -= player.speed * 0.7;
      }
      if (keys.ArrowDown) {
        updatedPlayer.y += player.speed * 0.7;
      }

      // Keep player within canvas bounds
      updatedPlayer.x = Math.max(
        0,
        Math.min(canvas.width - player.width, updatedPlayer.x)
      );
      updatedPlayer.y = Math.max(
        0,
        Math.min(canvas.height - player.height, updatedPlayer.y)
      );

      setPlayer(updatedPlayer);

      // Draw player
      drawPlayer(ctx, updatedPlayer);

      // Generate new asteroids
      if (Math.random() < settings.asteroidFrequency) {
        setAsteroids((prev) => [...prev, generateAsteroid(canvas)]);
      }

      // Generate new cosmic dust
      if (Math.random() < settings.dustFrequency) {
        setCosmicDusts((prev) => [...prev, generateCosmicDust(canvas)]);
      }

      // Update and draw asteroids
      let newAsteroids = [...asteroids];

      for (let i = 0; i < newAsteroids.length; i++) {
        const asteroid = newAsteroids[i];
        asteroid.y += asteroid.speed;
        asteroid.rotation += asteroid.rotationSpeed;

        // Remove asteroids that are off screen
        if (asteroid.y > canvas.height) {
          newAsteroids.splice(i, 1);
          i--;
          continue;
        }

        // Check for collision with player
        if (checkCollision(updatedPlayer, asteroid)) {
          setGameState("gameOver");
          return;
        }

        drawAsteroid(ctx, asteroid);
      }

      setAsteroids(newAsteroids);

      // Update and draw cosmic dust
      let newCosmicDusts = [...cosmicDusts];
      let newScore = score;

      for (let i = 0; i < newCosmicDusts.length; i++) {
        const dust = newCosmicDusts[i];
        dust.y += dust.speed;

        // Remove dusts that are off screen
        if (dust.y > canvas.height) {
          newCosmicDusts.splice(i, 1);
          i--;
          continue;
        }

        // Check for collection by player
        if (!dust.collected && checkCollision(updatedPlayer, dust)) {
          dust.collected = true;
          newScore += dust.value;
          newCosmicDusts.splice(i, 1);
          i--;
          continue;
        }

        if (!dust.collected) {
          drawCosmicDust(ctx, dust);
        }
      }

      setCosmicDusts(newCosmicDusts);
      setScore(newScore);

      // Update distance traveled
      const newDistance = distance + backgroundSpeed;
      setDistance(newDistance);

      // Check if we've reached the finish line
      if (newDistance >= maxDistance) {
        setGameState("gameOver");
        return;
      }

      // Draw UI
      drawUI(ctx, canvas, newScore, newDistance, maxDistance);

      // Loop
      animationFrameRef.current = requestAnimationFrame(gameLoop);
    };

    animationFrameRef.current = requestAnimationFrame(gameLoop);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [
    gameState,
    player,
    asteroids,
    cosmicDusts,
    keys,
    score,
    distance,
    difficulty,
    fieldLength,
    backgroundSpeed,
    backgroundPosition,
  ]);

  // Drawing functions
  const drawBackground = (
    ctx: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement
  ) => {
    // Draw space background
    ctx.fillStyle = "#0c0c20";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw stars
    const starCount = 100;
    for (let i = 0; i < starCount; i++) {
      const x = Math.random() * canvas.width;
      const y =
        (Math.random() * canvas.height * 2 + backgroundPosition) %
        canvas.height;
      const size = Math.random() * 2;
      const opacity = Math.random() * 0.8 + 0.2;

      ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
      ctx.beginPath();
      ctx.arc(x, y, size, 0, Math.PI * 2);
      ctx.fill();
    }

    // Draw distant nebulae
    drawNebula(
      ctx,
      canvas.width * 0.2,
      (canvas.height * 0.3 + backgroundPosition * 0.1) % canvas.height,
      100,
      "rgba(75, 0, 130, 0.3)"
    );
    drawNebula(
      ctx,
      canvas.width * 0.8,
      (canvas.height * 0.7 + backgroundPosition * 0.15) % canvas.height,
      150,
      "rgba(0, 100, 130, 0.2)"
    );

    // Update background position
    setBackgroundPosition(
      (prev) => (prev + backgroundSpeed) % (canvas.height * 2)
    );
  };

  const drawNebula = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    size: number,
    color: string
  ) => {
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, size);
    gradient.addColorStop(0, color);
    gradient.addColorStop(1, "transparent");

    ctx.fillStyle = gradient;
    ctx.fillRect(x - size, y - size, size * 2, size * 2);
  };

  const drawPlayer = (ctx: CanvasRenderingContext2D, player: Player) => {
    // Draw engine glow
    const engineGlow = ctx.createRadialGradient(
      player.x + player.width / 2,
      player.y + player.height + 5,
      0,
      player.x + player.width / 2,
      player.y + player.height + 5,
      20
    );
    engineGlow.addColorStop(0, "rgba(255, 165, 0, 0.8)");
    engineGlow.addColorStop(1, "transparent");

    ctx.fillStyle = engineGlow;
    ctx.beginPath();
    ctx.ellipse(
      player.x + player.width / 2,
      player.y + player.height + 5,
      20,
      30,
      0,
      0,
      Math.PI * 2
    );
    ctx.fill();

    // Draw spacecraft
    const img = new Image();
    img.src = player.spacecraft.image;

    ctx.save();
    ctx.translate(player.x + player.width / 2, player.y + player.height / 2);
    ctx.drawImage(
      img,
      -player.width / 2,
      -player.height / 2,
      player.width,
      player.height
    );
    ctx.restore();
  };

  const drawAsteroid = (ctx: CanvasRenderingContext2D, asteroid: Asteroid) => {
    ctx.save();
    ctx.translate(
      asteroid.x + asteroid.size / 2,
      asteroid.y + asteroid.size / 2
    );
    ctx.rotate((asteroid.rotation * Math.PI) / 180);

    // Draw asteroid
    ctx.fillStyle = "#666";
    ctx.beginPath();
    const segments = 8;
    for (let i = 0; i < segments; i++) {
      const angle = (i / segments) * Math.PI * 2;
      const radiusVariation = 0.8 + Math.sin(i * 3) * 0.2;
      const radius = (asteroid.size / 2) * radiusVariation;
      const x = Math.cos(angle) * radius;
      const y = Math.sin(angle) * radius;

      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }
    ctx.closePath();
    ctx.fill();

    // Draw craters
    ctx.fillStyle = "#555";
    for (let i = 0; i < 3; i++) {
      const craterSize = asteroid.size * (0.1 + Math.random() * 0.1);
      const angle = Math.random() * Math.PI * 2;
      const distance = asteroid.size * 0.3 * Math.random();
      const x = Math.cos(angle) * distance;
      const y = Math.sin(angle) * distance;

      ctx.beginPath();
      ctx.arc(x, y, craterSize, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  };

  const drawCosmicDust = (ctx: CanvasRenderingContext2D, dust: CosmicDust) => {
    // Create glow effect
    const gradient = ctx.createRadialGradient(
      dust.x + dust.size / 2,
      dust.y + dust.size / 2,
      0,
      dust.x + dust.size / 2,
      dust.y + dust.size / 2,
      dust.size
    );

    gradient.addColorStop(0, "rgba(30, 255, 255, 0.8)");
    gradient.addColorStop(0.5, "rgba(70, 130, 255, 0.6)");
    gradient.addColorStop(1, "transparent");

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(
      dust.x + dust.size / 2,
      dust.y + dust.size / 2,
      dust.size,
      0,
      Math.PI * 2
    );
    ctx.fill();

    // Add sparkle effect
    ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
    ctx.beginPath();
    ctx.arc(
      dust.x + dust.size / 2,
      dust.y + dust.size / 2,
      dust.size * 0.3,
      0,
      Math.PI * 2
    );
    ctx.fill();
  };

  const drawUI = (
    ctx: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement,
    score: number,
    distance: number,
    maxDistance: number
  ) => {
    // Draw score
    ctx.fillStyle = "white";
    ctx.font = "20px 'Exo 2', sans-serif";
    ctx.textAlign = "left";
    ctx.fillText(`Score: ${score}`, 20, 30);

    // Draw progress bar
    const progressBarWidth = canvas.width * 0.3;
    const progressBarHeight = 15;
    const progressBarX = canvas.width - progressBarWidth - 20;
    const progressBarY = 20;
    const progressPercentage = Math.min(distance / maxDistance, 1);

    // Draw background
    ctx.fillStyle = "rgba(255, 255, 255, 0.2)";
    ctx.fillRect(
      progressBarX,
      progressBarY,
      progressBarWidth,
      progressBarHeight
    );

    // Draw progress
    ctx.fillStyle = "#3498db";
    ctx.fillRect(
      progressBarX,
      progressBarY,
      progressBarWidth * progressPercentage,
      progressBarHeight
    );

    // Draw border
    ctx.strokeStyle = "white";
    ctx.lineWidth = 2;
    ctx.strokeRect(
      progressBarX,
      progressBarY,
      progressBarWidth,
      progressBarHeight
    );

    // Draw percentage text
    ctx.fillStyle = "white";
    ctx.textAlign = "right";
    ctx.fillText(
      `${Math.floor(progressPercentage * 100)}%`,
      progressBarX - 10,
      progressBarY + 12
    );
  };

  // Resize handler
  useEffect(() => {
    const handleResize = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      // Set canvas size
      canvas.width = canvas.clientWidth;
      canvas.height = canvas.clientHeight;

      // Adjust player position when window resizes
      if (player) {
        setPlayer((prev) => {
          if (!prev) return null;
          return {
            ...prev,
            x: Math.min(prev.x, canvas.width - prev.width),
            y: Math.min(prev.y, canvas.height - prev.height),
          };
        });
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [player]);

  // Clean up animation frame on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  // Custom styles
  const styles = {
    container: {
      display: "flex",
      flexDirection: "column" as const,
      alignItems: "center",
      minHeight: "100vh",
      background: "linear-gradient(to bottom, #0f0c29, #302b63, #24243e)",
      fontFamily: "'Exo 2', sans-serif",
      color: "white",
      padding: "20px",
      boxSizing: "border-box" as const,
      position: "relative" as const,
    },
    header: {
      textAlign: "center" as const,
      marginBottom: "20px",
    },
    title: {
      fontSize: "3rem",
      margin: "0",
      background: "linear-gradient(to right, #3498db, #8e44ad)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      textShadow: "0 0 20px rgba(52, 152, 219, 0.5)",
    },
    subtitle: {
      fontSize: "1.2rem",
      opacity: 0.8,
      marginTop: "10px",
    },
    gameArea: {
      width: "100%",
      maxWidth: "800px",
      height: "600px",
      position: "relative" as const,
      boxShadow: "0 0 30px rgba(52, 152, 219, 0.3)",
      borderRadius: "10px",
      overflow: "hidden",
    },
    canvas: {
      display: "block",
      width: "100%",
      height: "100%",
      backgroundColor: "#0c0c20",
      borderRadius: "10px",
    },
    menu: {
      position: "absolute" as const,
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column" as const,
      justifyContent: "center",
      alignItems: "center",
      background: "rgba(12, 12, 32, 0.9)",
      backdropFilter: "blur(5px)",
      zIndex: 10,
      borderRadius: "10px",
    },
    menuTitle: {
      fontSize: "2.5rem",
      textAlign: "center" as const,
      marginBottom: "20px",
      color: "#3498db",
      textShadow: "0 0 10px rgba(52, 152, 219, 0.7)",
    },
    optionsContainer: {
      display: "flex",
      flexDirection: "column" as const,
      width: "80%",
      maxWidth: "400px",
      margin: "20px 0",
    },
    optionRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "15px",
    },
    optionLabel: {
      fontSize: "1.2rem",
      marginRight: "20px",
    },
    select: {
      background: "rgba(255, 255, 255, 0.1)",
      border: "1px solid rgba(52, 152, 219, 0.5)",
      color: "white",
      padding: "8px 15px",
      borderRadius: "5px",
      outline: "none",
      fontSize: "1rem",
      cursor: "pointer",
      transition: "all 0.3s ease",
    },
    spacecraftGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
      gap: "15px",
      width: "100%",
      marginTop: "10px",
      maxHeight: "200px",
      overflowY: "auto" as const,
      padding: "10px",
      boxSizing: "border-box" as const,
    },
    spacecraftCard: {
      display: "flex",
      flexDirection: "column" as const,
      alignItems: "center",
      padding: "10px",
      border: "1px solid rgba(52, 152, 219, 0.5)",
      borderRadius: "8px",
      cursor: "pointer",
      transition: "all 0.3s ease",
    },
    spacecraftCardSelected: {
      backgroundColor: "rgba(52, 152, 219, 0.3)",
      boxShadow: "0 0 10px rgba(52, 152, 219, 0.7)",
    },
    spacecraftImage: {
      width: "50px",
      height: "50px",
      marginBottom: "5px",
    },
    spacecraftName: {
      fontSize: "0.9rem",
      textAlign: "center" as const,
      marginBottom: "5px",
    },
    spacecraftStats: {
      fontSize: "0.7rem",
      opacity: 0.8,
      textAlign: "center" as const,
    },
    button: {
      backgroundColor: "#3498db",
      color: "white",
      border: "none",
      borderRadius: "5px",
      padding: "12px 30px",
      fontSize: "1.2rem",
      cursor: "pointer",
      margin: "10px",
      transition: "all 0.3s ease",
      boxShadow: "0 0 15px rgba(52, 152, 219, 0.5)",
    },
    buttonHover: {
      backgroundColor: "#2980b9",
      transform: "translateY(-2px)",
      boxShadow: "0 0 20px rgba(52, 152, 219, 0.7)",
    },
    gameOverOverlay: {
      position: "absolute" as const,
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column" as const,
      justifyContent: "center",
      alignItems: "center",
      background: "rgba(0, 0, 0, 0.7)",
      backdropFilter: "blur(5px)",
      zIndex: 10,
    },
    gameOverTitle: {
      fontSize: "2.5rem",
      marginBottom: "20px",
      color: "#e74c3c",
      textShadow: "0 0 10px rgba(231, 76, 60, 0.7)",
    },
    gameOverText: {
      fontSize: "1.2rem",
      marginBottom: "10px",
    },
    pauseButton: {
      position: "absolute" as const,
      top: "10px",
      right: "10px",
      backgroundColor: "rgba(52, 152, 219, 0.3)",
      border: "none",
      color: "white",
      padding: "5px 10px",
      fontSize: "1rem",
      borderRadius: "5px",
      cursor: "pointer",
      zIndex: 5,
    },
    controls: {
      textAlign: "center" as const,
      marginTop: "20px",
      padding: "10px",
      background: "rgba(255, 255, 255, 0.1)",
      borderRadius: "8px",
      fontSize: "0.9rem",
      lineHeight: "1.5",
    },
    pausedOverlay: {
      position: "absolute" as const,
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column" as const,
      justifyContent: "center",
      alignItems: "center",
      background: "rgba(0, 0, 0, 0.5)",
      backdropFilter: "blur(3px)",
      zIndex: 10,
    },
    pausedText: {
      fontSize: "2.5rem",
      color: "white",
      textShadow: "0 0 10px rgba(255, 255, 255, 0.7)",
    },
    pauseInstructions: {
      marginTop: "20px",
      fontSize: "1.2rem",
      opacity: 0.8,
    },
    mobileControls: {
      display: "none",
      position: "absolute" as const,
      bottom: "20px",
      left: "50%",
      transform: "translateX(-50%)",
      width: "80%",
      maxWidth: "300px",
      zIndex: 5,
      justifyContent: "center",
      "@media (max-width: 768px)": {
        display: "flex",
      },
    },
    directionButton: {
      width: "60px",
      height: "60px",
      margin: "5px",
      borderRadius: "50%",
      backgroundColor: "rgba(52, 152, 219, 0.3)",
      border: "2px solid rgba(52, 152, 219, 0.5)",
      color: "white",
      fontSize: "1.5rem",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      cursor: "pointer",
      userSelect: "none" as const,
      touchAction: "manipulation" as const,
    },
    progressText: {
      fontSize: "0.8rem",
      position: "absolute" as const,
      top: "40px",
      right: "20px",
      color: "white",
    },
  };
  // Touch controls for mobile
  const handleTouchStart = (direction: keyof typeof keys) => {
    setKeys((prev) => ({ ...prev, [direction]: true }));
  };
  const handleTouchEnd = (direction: keyof typeof keys) => {
    setKeys((prev) => ({ ...prev, [direction]: false }));
  };
  // Button hover state
  const [buttonHover, setButtonHover] = useState(false);
  // Render function
  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Cosmic Racer</h1>
        <p style={styles.subtitle}>
          Race through space, avoid asteroids, collect cosmic dust!
        </p>
      </div>
      <div style={styles.gameArea}>
        <canvas ref={canvasRef} style={styles.canvas} />

        {gameState === "playing" && (
          <button
            style={styles.pauseButton}
            onClick={() => setGameState("paused")}
          >
            Pause
          </button>
        )}

        {/* Mobile controls */}
        {gameState === "playing" && (
          <div
            style={{
              ...styles.mobileControls,
              display: window.innerWidth <= 768 ? "flex" : "none",
            }}
          >
            <div
              style={styles.directionButton}
              onTouchStart={() => handleTouchStart("ArrowLeft")}
              onTouchEnd={() => handleTouchEnd("ArrowLeft")}
            >
              ←
            </div>
            <div style={{ display: "flex", flexDirection: "column" as const }}>
              <div
                style={styles.directionButton}
                onTouchStart={() => handleTouchStart("ArrowUp")}
                onTouchEnd={() => handleTouchEnd("ArrowUp")}
              >
                ↑
              </div>
              <div
                style={styles.directionButton}
                onTouchStart={() => handleTouchStart("ArrowDown")}
                onTouchEnd={() => handleTouchEnd("ArrowDown")}
              >
                ↓
              </div>
            </div>
            <div
              style={styles.directionButton}
              onTouchStart={() => handleTouchStart("ArrowRight")}
              onTouchEnd={() => handleTouchEnd("ArrowRight")}
            >
              →
            </div>
          </div>
        )}

        {/* Game menu */}
        {gameState === "menu" && (
          <div style={styles.menu}>
            <h2 style={styles.menuTitle}>Cosmic Racer</h2>

            <div style={styles.optionsContainer}>
              <div style={styles.optionRow}>
                <label style={styles.optionLabel}>Difficulty:</label>
                <select
                  style={styles.select}
                  value={difficulty}
                  onChange={(e) => setDifficulty(e.target.value as Difficulty)}
                >
                  <option value="easy">Easy</option>
                  <option value="medium">Medium</option>
                  <option value="hard">Hard</option>
                </select>
              </div>

              <div style={styles.optionRow}>
                <label style={styles.optionLabel}>Field Length:</label>
                <select
                  style={styles.select}
                  value={fieldLength}
                  onChange={(e) =>
                    setFieldLength(e.target.value as FieldLength)
                  }
                >
                  <option value="short">Short</option>
                  <option value="medium">Medium</option>
                  <option value="long">Long</option>
                </select>
              </div>

              <div style={{ marginTop: "20px" }}>
                <label style={styles.optionLabel}>Select Spacecraft:</label>
                <div style={styles.spacecraftGrid}>
                  {spacecrafts.map((craft) => (
                    <div
                      key={craft.id}
                      style={{
                        ...styles.spacecraftCard,
                        ...(selectedSpacecraftId === craft.id
                          ? styles.spacecraftCardSelected
                          : {}),
                      }}
                      onClick={() => setSelectedSpacecraftId(craft.id)}
                    >
                      <img
                        src={craft.image}
                        alt={craft.name}
                        style={styles.spacecraftImage}
                      />
                      <div style={styles.spacecraftName}>{craft.name}</div>
                      <div style={styles.spacecraftStats}>
                        Speed: {craft.speed}
                        <br />
                        Maneuverability: {craft.maneuverability}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <button
              style={{
                ...styles.button,
                ...(buttonHover ? styles.buttonHover : {}),
              }}
              onMouseEnter={() => setButtonHover(true)}
              onMouseLeave={() => setButtonHover(false)}
              onClick={initializeGame}
            >
              Start Game
            </button>

            <div style={styles.controls}>
              <strong>Controls:</strong> Use arrow keys to move your spacecraft.
              <br />
              Press P to pause the game. Collect cosmic dust and avoid
              asteroids!
            </div>
          </div>
        )}

        {/* Paused overlay */}
        {gameState === "paused" && (
          <div style={styles.pausedOverlay}>
            <div style={styles.pausedText}>Game Paused</div>
            <div style={styles.pauseInstructions}>Press P to resume</div>
            <button
              style={styles.button}
              onClick={() => setGameState("playing")}
            >
              Resume
            </button>
            <button
              style={{ ...styles.button, backgroundColor: "#e74c3c" }}
              onClick={() => setGameState("menu")}
            >
              Quit
            </button>
          </div>
        )}

        {/* Game over screen */}
        {gameState === "gameOver" && (
          <div style={styles.gameOverOverlay}>
            <h2 style={styles.gameOverTitle}>
              {distance >= fieldLengthSettings[fieldLength]
                ? "Finish Line Reached!"
                : "Game Over"}
            </h2>
            <div style={styles.gameOverText}>Score: {score}</div>
            <div style={styles.gameOverText}>
              Distance: {Math.floor(distance)} /{" "}
              {fieldLengthSettings[fieldLength]}
            </div>
            <button style={styles.button} onClick={initializeGame}>
              Play Again
            </button>
            <button
              style={{ ...styles.button, backgroundColor: "#2ecc71" }}
              onClick={() => setGameState("menu")}
            >
              Back to Menu
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
